<template>
  <div class="ag-grid-search-filter">
    <!--
      The suggestions drop down is hung off the body rather than off this filter, so a click on one
      of them reads as a click outside the popup and ag-grid would put the popup away mid-search.
      This class is what tells it the drop down is part of this filter after all.
    -->
    <el-autocomplete
      ref="search"
      v-model="keyword"
      class="search-input"
      popper-class="ag-custom-component-popup"
      size="small"
      icon="search"
      :placeholder="placeholder"
      :fetch-suggestions="searchRecords"
      value-key="label"
      :trigger-on-focus="false"
      :select-when-unmatched="false"
      @select="addRecord"
    />
    <div v-if="pending.length" class="search-selection">
      <el-tag
        v-for="record in pending"
        :key="record.id"
        :closable="true"
        type="primary"
        @close="removeRecord(record)">
        {{ record.name }}
      </el-tag>
    </div>
    <div v-else class="search-selection-empty">
      {{ emptyText }}
    </div>
    <div class="search-filter-actions">
      <el-button v-if="!singleSelect" size="small" type="primary" :disabled="!dirty" @click="applySelection">Apply</el-button>
      <el-button size="small" :disabled="!anySelection" @click="clearSelection">Clear</el-button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

/**
 * Column filter for the columns whose values are records of their own - vendors, batches. A set
 * filter can only offer what the store already holds, which on a paged list is whatever happened to
 * be on the pages fetched so far, so this one searches the API as the user types. Its model holds
 * the ids of what was picked, which is what the transaction query filters on.
 *
 * Picking and removing records builds up what the popup shows; Apply is what hands it to the grid,
 * so several records are one trip to the API rather than one each. A column read one record at a
 * time has nothing to build up, so there picking one is applying it.
 *
 * Configured through the column's filterParams:
 *   searchAction    Vuex action that searches the records, e.g. 'fetchVendorsWithQuery'
 *   entityKey       Key the action returns the records under, e.g. 'vendors'
 *   placeholder     Placeholder for the search box
 *   emptyText       Shown while nothing is selected
 *   singleSelect    Whether the column is filtered by one record at a time, the next one picked
 *                   taking the place of the last rather than joining it
 *   serverFiltered  Whether the query carries this column's criteria, in which case the rows the API
 *                   returned are all kept rather than filtered a second time here
 */
export default defineComponent({
  name: 'AgGridSearchFilter',

  data () {
    return {
      keyword: '',
      // What the filter is asking for, which is what was applied - the model the grid reads
      selected: [],
      // What the popup is showing, which reaches the grid on Apply and not before
      pending: [],
      // Handed over by the grid when the popup goes up, and how it is put away again
      hidePopup: null
    }
  },

  computed: {
    placeholder () {
      return this.params.placeholder || 'Search'
    },
    emptyText () {
      return this.params.emptyText || 'Search for a value to filter by'
    },
    singleSelect () {
      return !!this.params.singleSelect
    },
    /**
     * Whether the popup holds records the filter is not asking for yet, either way round, which is
     * what there is to apply
     * @return {boolean}
     */
    dirty () {
      if (this.pending.length !== this.selected.length) return true
      const applied = new Set(this.selected.map(record => record.id))
      return this.pending.some(record => !applied.has(record.id))
    },
    /**
     * Whether there is anything to clear: what the popup holds, or what the column is filtered by
     * with the popup already emptied
     * @return {boolean}
     */
    anySelection () {
      return this.pending.length > 0 || this.selected.length > 0
    }
  },

  methods: {
    /**
     * Suggestions for the search box, straight from the API
     * @param query     What the user typed
     * @param callback  Callback the suggestions are handed to
     */
    searchRecords (query, callback) {
      const search = (query || '').trim()
      const { searchAction, entityKey } = this.params
      if (!searchAction) return callback([])
      this.$store.dispatch(searchAction, { search })
        .then(response => {
          const records = response && response.entities && response.entities[entityKey]
            ? Object.values(response.entities[entityKey])
            : []
          callback(records.map(record => ({ label: record.name, value: record.id })))
        })
        .catch(error => {
          console.log(error)
          callback([])
        })
    },
    /**
     * Adds the record picked from the suggestions to what the popup is showing. Nothing is filtered
     * on until Apply, so several can be picked for one trip to the API - unless the column is read
     * one record at a time, where the record picked is the whole of it and is asked for at once.
     * @param suggestion  { label, value }
     */
    addRecord (suggestion) {
      if (!(suggestion && suggestion.value)) return
      const record = { id: suggestion.value, name: suggestion.label }
      this.keyword = ''
      if (this.singleSelect) {
        this.pending = [record]
        return this.applySelection()
      }
      if (!this.pending.some(r => r.id === record.id)) {
        this.pending.push(record)
      }
    },
    // Takes the record off what the popup is showing, which Apply is what asks for. With one record
    // to the column there is no Apply to press, so taking it off is asking for the column back.
    removeRecord (record) {
      this.pending = this.pending.filter(r => r.id !== record.id)
      if (this.singleSelect) this.clearSelection()
    },
    /**
     * Asks for the whole column back: what the popup holds goes, and so does what the column was
     * filtered by. It is the end of picking records, so the popup is put away with it.
     */
    clearSelection () {
      if (!this.anySelection) return
      this.pending = []
      this.keyword = ''
      this.applyPending()
      this.closePopup()
    },
    /**
     * Hands what the popup holds to the grid, the one thing here that reaches the API, and puts the
     * popup away - the user is done picking records
     */
    applySelection () {
      // Nothing to hand over is the record the column is already filtered by being picked again,
      // which is still the user saying they are done with the popup
      this.applyPending()
      this.closePopup()
    },
    applyPending () {
      if (!this.dirty) return
      this.selected = this.pending.slice()
      this.filterChanged()
    },
    /**
     * Puts the filter popup away. Nothing else here does: the popup stays up while records are
     * being picked and taken off, so a run of them is one trip to the API.
     *
     * The popup is the column menu, closed through the grid rather than through the hidePopup a
     * filter is normally handed: this one is shown from the floating filter, which opens the menu
     * on the filter tab alone - and a menu of one tab has already shown it by the time the popup
     * exists to be closed, so what reaches afterGuiAttached here is nothing at all.
     */
    closePopup () {
      if (this.hidePopup) {
        this.hidePopup()
        this.hidePopup = null
        return
      }
      if (this.params.api) this.params.api.hidePopupMenu()
    },
    filterChanged () {
      this.params.filterChangedCallback()
    },

    /* ag-grid IFilterComp */

    isFilterActive () {
      return this.selected.length > 0
    },
    /**
     * Where the query is built from what was picked here, the API has already applied it to the rows
     * the grid was given and all of them stay - what was picked are record ids, which is not what
     * the column shows. On the views that still filter client side the record's name is.
     */
    doesFilterPass (params) {
      if (this.params.serverFiltered) return true
      if (!this.selected.length) return true
      const value = this.params.valueGetter(params.node)
      return this.selected.some(record => record.name === value)
    },
    /**
     * @return {Object|null} The ids the API is queried with, plus their names for what the filter
     *                       and the floating filter display
     */
    getModel () {
      if (!this.selected.length) return null
      return {
        filterType: this.params.colDef ? this.params.colDef.colId : 'search',
        values: this.selected.map(record => record.id),
        names: this.selected.map(record => record.name)
      }
    },
    setModel (model) {
      const ids = model && Array.isArray(model.values) ? model.values : []
      const names = model && Array.isArray(model.names) ? model.names : []
      const records = ids.map((id, index) => ({ id, name: names[index] }))
      // More records than the column is read by is it being asked for more than it offers, so the
      // first of them is what it is read as
      this.selected = this.singleSelect ? records.slice(0, 1) : records
      // The grid is saying what this filter is asking for, so the popup opens on it with nothing
      // left to apply - an edit that was never applied is not what the rows on screen answer to
      this.pending = this.selected.slice()
      this.keyword = ''
    },
    getModelAsString () {
      return this.selected.map(record => record.name).join(', ')
    },
    /**
     * ag-grid IFilterComp: the popup is up. It hands over the means of putting it away where it
     * has one to hand over, which Apply and Clear use - nothing else here closes the popup.
     * @param params  { hidePopup }
     */
    afterGuiAttached (params) {
      this.hidePopup = (params && params.hidePopup) || null
      this.$nextTick(() => {
        const input = this.$el.querySelector('input')
        if (input) input.focus()
      })
    }
  }
})
</script>

<style>
  .ag-grid-search-filter {
    padding: 8px;
    width: 260px;
  }
  .search-input {
    width: 100%;
  }
  .search-selection {
    max-height: 160px;
    overflow-y: auto;
    margin-top: 8px !important;
  }
  .search-selection > .el-tag {
    margin: 0 4px 4px 0 !important;
    max-width: 100%;
  }
  .search-selection-empty {
    margin-top: 8px;
    color: #a7a7a7;
    font-size: 12px;
  }
  .search-filter-actions {
    margin-top: 8px;
    text-align: right;
    border-top: 1px solid #e2e2e2;
    padding-top: 8px;
  }
</style>
